import { ExpressionCondition } from '../../constants/expressions.constants'

enum ElementState {
  VISIBLE = 'visible',
  HIDDEN = 'hidden',
  ACTIVE = 'active',
  DISABLED = 'disabled',
  APPLIED = 'applied',
  NOT_APPLIED = 'not applied',
  TRIGGERED = 'triggered',
  NOT_TRIGGERED = 'not triggered',
  SELECTED = 'selected',
  NOT_SELECTED = 'not selected',
}

const ACTIVE_ELEMENT_BG_COLOR = 'bg-green-50'
const ACTIVE_ELEMENT_TEXT_COLOR = 'text-green-500'
const INACTIVE_ELEMENT_BG_COLOR = 'bg-neutral-100'
const INACTIVE_ELEMENT_TEXT_COLOR = 'text-neutral-500'

const ELEMENT_TEXT = 'Element is'
const CONDITION_TEXT = 'Condition is'
const ACTION_TEXT = 'Action is'
const PROTOTYPE_TEXT = 'Prototype is'

type ElementStateConfig = {
  text: string
  state: ElementState
  icon: string
  textColor: string
  bgColor: string
  phosphorIcon?: boolean
}
type ExpressionConditionStatesMap = Map<boolean, ElementStateConfig>

const OUTCOME_VISIBLE_CONFIG: ElementStateConfig = {
  text: ELEMENT_TEXT,
  state: ElementState.VISIBLE,
  icon: 'outcome-visible',
  textColor: ACTIVE_ELEMENT_TEXT_COLOR,
  bgColor: ACTIVE_ELEMENT_BG_COLOR,
}

const OUTCOME_HIDDEN_CONFIG: ElementStateConfig = {
  text: ELEMENT_TEXT,
  state: ElementState.HIDDEN,
  icon: 'outcome-hidden',
  textColor: INACTIVE_ELEMENT_TEXT_COLOR,
  bgColor: INACTIVE_ELEMENT_BG_COLOR,
}

const hideExpressionStatesMap: ExpressionConditionStatesMap = new Map([
  [false, OUTCOME_VISIBLE_CONFIG],
  [true, OUTCOME_HIDDEN_CONFIG],
])

const OUTCOME_ACTIVE_CONFIG: ElementStateConfig = {
  text: ELEMENT_TEXT,
  state: ElementState.ACTIVE,
  icon: 'outcome-active',
  textColor: ACTIVE_ELEMENT_TEXT_COLOR,
  bgColor: ACTIVE_ELEMENT_BG_COLOR,
}

const OUTCOME_DISABLED_CONFIG: ElementStateConfig = {
  text: ELEMENT_TEXT,
  state: ElementState.DISABLED,
  icon: 'outcome-disabled',
  textColor: INACTIVE_ELEMENT_TEXT_COLOR,
  bgColor: INACTIVE_ELEMENT_BG_COLOR,
}

const disableExpressionStatesMap: ExpressionConditionStatesMap = new Map([
  [false, OUTCOME_ACTIVE_CONFIG],
  [true, OUTCOME_DISABLED_CONFIG],
])

const OUTCOME_CONDITION_NOT_APPLIED: ElementStateConfig = {
  text: CONDITION_TEXT,
  state: ElementState.NOT_APPLIED,
  icon: 'outcome-hidden',
  textColor: INACTIVE_ELEMENT_TEXT_COLOR,
  bgColor: INACTIVE_ELEMENT_BG_COLOR,
}

const OUTCOME_CONDITION_APPLIED: ElementStateConfig = {
  text: CONDITION_TEXT,
  state: ElementState.APPLIED,
  icon: 'outcome-visible',
  textColor: ACTIVE_ELEMENT_TEXT_COLOR,
  bgColor: ACTIVE_ELEMENT_BG_COLOR,
}

const conditionalStyleStatesMap: ExpressionConditionStatesMap = new Map([
  [false, OUTCOME_CONDITION_NOT_APPLIED],
  [true, OUTCOME_CONDITION_APPLIED],
])

const OUTCOME_TRIGGERED_CONFIG: ElementStateConfig = {
  text: ACTION_TEXT,
  state: ElementState.TRIGGERED,
  icon: 'ph ph-lightning',
  phosphorIcon: true,
  textColor: ACTIVE_ELEMENT_TEXT_COLOR,
  bgColor: ACTIVE_ELEMENT_BG_COLOR,
}

const OUTCOME_NOT_TRIGGERED_CONFIG: ElementStateConfig = {
  text: ACTION_TEXT,
  state: ElementState.NOT_TRIGGERED,
  icon: 'ph ph-lightning-slash',
  phosphorIcon: true,
  textColor: INACTIVE_ELEMENT_TEXT_COLOR,
  bgColor: INACTIVE_ELEMENT_BG_COLOR,
}

const triggerExpressionStatesMap: ExpressionConditionStatesMap = new Map([
  [false, OUTCOME_NOT_TRIGGERED_CONFIG],
  [true, OUTCOME_TRIGGERED_CONFIG],
])

const OUTCOME_SELECTED_CONFIG: ElementStateConfig = {
  text: PROTOTYPE_TEXT,
  state: ElementState.SELECTED,
  icon: 'outcome-visible',
  textColor: ACTIVE_ELEMENT_TEXT_COLOR,
  bgColor: ACTIVE_ELEMENT_BG_COLOR,
}

const OUTCOME_NOT_SELECTED_CONFIG: ElementStateConfig = {
  text: PROTOTYPE_TEXT,
  state: ElementState.NOT_SELECTED,
  icon: 'outcome-hidden',
  textColor: INACTIVE_ELEMENT_TEXT_COLOR,
  bgColor: INACTIVE_ELEMENT_BG_COLOR,
}

const prototypeSelectionStatesMap: ExpressionConditionStatesMap = new Map([
  [false, OUTCOME_NOT_SELECTED_CONFIG],
  [true, OUTCOME_SELECTED_CONFIG],
])

const elementStateMap: Map<ExpressionCondition, ExpressionConditionStatesMap> = new Map([
  [ExpressionCondition.HIDE, hideExpressionStatesMap],
  [ExpressionCondition.DISABLED, disableExpressionStatesMap],
  [ExpressionCondition.CONDITIONAL_STYLING, conditionalStyleStatesMap],
  [ExpressionCondition.TRIGGER_CONDITION, triggerExpressionStatesMap],
  [ExpressionCondition.PROTOTYPE_SELECTION, prototypeSelectionStatesMap],
])

export function getElementStateConfig(
  conditionType: ExpressionCondition,
  isBoolean: boolean
): ElementStateConfig {
  return elementStateMap.get(conditionType)?.get(isBoolean)
}
